import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useDeleteEntry } from '@/pages/Schedule/hooks';
import { useDraftEntryStore } from '@/pages/Schedule/stores';
import type { DecoratedEntry } from '@/pages/Schedule/types';
import { isRecurringEntry } from '@/pages/Schedule/utils';
import { useToast } from '@/ui';
import { useIsColumnLayout } from '../../../hooks/useIsColumnLayout';
import { useJobCache } from '../../../hooks/useJobCache';
import type { Job } from '../../../types';
import { Panel } from '../Panel';
import { EntryCard } from './EntryCard';
import { FallbackNotice } from './FallbackNotice';
import { HeaderCard } from './HeaderCard';
import { didJobUseFallback } from './utils';

type Props = {
  job: Job;
  isPublishLoading: boolean;
  onPublish: () => void;
};

export const ResultsPanel = ({ job, isPublishLoading, onPublish }: Props) => {
  const entries = job.entries ?? [];
  const { deleteEntry, isPending: isDeleting } = useDeleteEntry();
  const editDraftEntry = useDraftEntryStore((store) => store.editDraftEntry);
  const toast = useToast();
  const { isColumnLayout } = useIsColumnLayout();
  const { t } = useTranslation('addContent');
  const cache = useJobCache(job.id);
  const showFallbackNotice = didJobUseFallback(job);
  const [confirmDeleteEntry, setConfirmDeleteEntry] =
    useState<DecoratedEntry | null>(null);

  const handleDelete = () => {
    if (confirmDeleteEntry) {
      deleteEntry(confirmDeleteEntry.id, {
        onError: () => toast.error(t('delete_error')),
        onSuccess: () => {
          cache.deleteEntry(confirmDeleteEntry.id);
          setConfirmDeleteEntry(null);
        },
      });
    }
  };

  const handleMessagesClick = (entry: DecoratedEntry) => {
    const isRecurring = isRecurringEntry(entry);

    editDraftEntry(entry, undefined, {
      launchAction: isRecurring ? undefined : 'create-message',
    });
  };

  return (
    <>
      <ConfirmDialog
        isLoading={isDeleting}
        isOpen={!!confirmDeleteEntry}
        title={t('delete_confirm', { name: confirmDeleteEntry?.title })}
        onCancel={() => setConfirmDeleteEntry(null)}
        onConfirm={handleDelete}
      />

      <Panel
        gap="3"
        overflowY={isColumnLayout ? 'initial' : 'auto'}
        pos="relative"
      >
        <HeaderCard
          count={entries.length}
          isPublishLoading={isPublishLoading}
          onPublish={onPublish}
        />

        {showFallbackNotice && <FallbackNotice />}

        {entries.map((entry) => (
          <EntryCard
            entry={entry}
            isConfirmingDelete={confirmDeleteEntry?.id === entry.id}
            key={entry.id}
            onDeleteClick={setConfirmDeleteEntry}
            onEditClick={editDraftEntry}
            onMessagesClick={handleMessagesClick}
          />
        ))}
      </Panel>
    </>
  );
};
